import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import amlProvider, {
	type TransactionAMLDetails,
} from "src/providers/aml-provider";
import type {
	AMLApproveRequest,
	AMLRecheckRequest,
	AMLRejectRequest,
	AMLTransactionsPagination,
	ListAMLTransactionsParams,
} from "src/types";

export function useAMLTransactions(params: ListAMLTransactionsParams) {
	return useQuery<AMLTransactionsPagination>({
		queryKey: ["aml-transactions", params],
		queryFn: () => amlProvider.listTransactions(params),
		staleTime: 30_000,
		refetchInterval: 60_000,
	});
}

export function useAMLTransactionDetails(transactionId: number | null) {
	return useQuery<TransactionAMLDetails>({
		queryKey: ["aml-transaction-details", transactionId],
		queryFn: () => {
			if (transactionId === null) {
				return Promise.reject(new Error("Transaction ID is required"));
			}
			return amlProvider.getTransactionDetails(transactionId);
		},
		enabled: transactionId !== null,
		staleTime: 30_000,
	});
}

// Deprecated: Use useAMLTransactions instead
export function useAMLHighRiskTransactions(params: ListAMLTransactionsParams) {
	return useAMLTransactions(params);
}

export function useApproveAMLTransaction() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: number; data: AMLApproveRequest }) =>
			amlProvider.approveTransaction(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["aml-transactions"] });
		},
	});
}

export function useRejectAMLTransaction() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: number; data: AMLRejectRequest }) =>
			amlProvider.rejectTransaction(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["aml-transactions"] });
		},
	});
}

export function useRecheckAMLTransaction() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: number; data: AMLRecheckRequest }) =>
			amlProvider.recheckTransaction(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["aml-transactions"] });
		},
	});
}

export function useSkipAMLTransaction() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: number; data: AMLApproveRequest }) =>
			amlProvider.skipTransaction(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["aml-transactions"] });
		},
	});
}
